// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

/**
 * How one analysis run was carried out, stage by stage.
 *
 * The activity row says a run succeeded and when. It cannot say where the time went, whether
 * the imagery was fetched or read back out of the stored basemap, or how many detections
 * promotion dropped for a stronger source — and until these rows were stored, neither could
 * anything else except the analyser's logs, for as long as that index kept them.
 */

/** One stage: what it moved, and how long it took. */
export type AnalysisStep = {
  /** imagery | detect | merge | promote | candidates. */
  name: string;
  seconds: number;
  /** The stage's counters, as the analyser tallied them. Keys vary by stage. */
  detail: Record<string, number>;
};

/** One imagery provider's stages within the run. */
export type AnalysisStepProvider = {
  provider: string;
  providerVersion: string | null;
  partial: boolean;
  error: string | null;
  steps: AnalysisStep[];
  /** The job these stages belong to; null for a result from before jobs existed. */
  jobId: string | null;
};

/**
 * One job of the run — a detection per provider and detector, the canopy, the elevations,
 * the hole pictures — with the times that place it on the round's clock.
 */
export type AnalysisStepJob = {
  id: string;
  /** detection | canopy-height | elevation | imagery-products. */
  kind: string;
  provider: string | null;
  detector: string | null;
  modelVersion: string | null;
  /** planned | dispatched | succeeded | partial | failed. */
  status: string;
  /** What a planned job is waiting for (an acquisition's source), or null. */
  waitingOn: string | null;
  requestedAt: string;
  dispatchedAt: string | null;
  finishedAt: string | null;
  workerSeconds: number | null;
  workerId: string | null;
  error: string | null;
  /** The course was decided from this job's answer — the map changed when it landed. */
  decided: boolean;
  /** Which try this is, from 1: a job the analyser lost mid-run is re-queued and counted. */
  attempt: number;
};

/** What the OSM import has stored for the run's course. */
export type AnalysisOsmFeatures = {
  greens: number;
  /** Hole playing lines (centrelines) — one per imported hole. */
  holes: number;
  bunkers: number;
  fairways: number;
};

/** What Golf i Sverige's published scorecard contributed to the run's course. */
export type AnalysisScorecardImport = {
  importedAtUtc: string;
  holes: number;
  tees: number;
};

export type AnalysisRunSteps = {
  runId: string;
  courseId: string;
  courseName: string | null;
  status: string;
  detector: string;
  modelVersion: string | null;
  startedAt: string;
  finishedAt: string | null;
  durationSeconds: number | null;
  error: string | null;
  providers: AnalysisStepProvider[];
  /** The round's jobs, oldest first; empty for a run from before jobs existed. */
  jobs: AnalysisStepJob[];
  /** When the run was requested — the clock to line other systems up against. */
  requestedAt: string | null;
  /** The correlation id every job, worker call and analyser log line of this round carries. */
  correlationId: string | null;
  /** What the OSM import has stored for the course; null when nothing was imported. */
  osmFeatures: AnalysisOsmFeatures | null;
  /** What Golf i Sverige's scorecard contributed; null when nothing — or when nobody answered. */
  scorecardImport: AnalysisScorecardImport | null;
};

export type AnalysisStepsState =
  | { status: 'loading' }
  | { status: 'ready'; run: AnalysisRunSteps }
  /** The run predates step recording, or its analyser never reported any. */
  | { status: 'empty' }
  | { status: 'forbidden' }
  | { status: 'failed' };

type Row = Record<string, unknown>;

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const num = (row: Row, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

/** Counters arrive as an object of whole numbers; anything else in there is ignored. */
const detailOf = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== 'object') return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      (entry): entry is [string, number] => typeof entry[1] === 'number' && Number.isFinite(entry[1])
    )
  );
};

const mapStep = (row: Row): AnalysisStep => ({
  name: str(row, 'name', 'Name') ?? 'unknown',
  seconds: num(row, 'seconds', 'Seconds') ?? 0,
  detail: detailOf(row.detail ?? row.Detail),
});

const mapProvider = (row: Row): AnalysisStepProvider => {
  const steps = row.steps ?? row.Steps;
  return {
    provider: str(row, 'provider', 'Provider') ?? 'unknown',
    providerVersion: str(row, 'providerVersion', 'ProviderVersion'),
    partial: (row.partial ?? row.Partial) === true,
    error: str(row, 'error', 'Error'),
    steps: (Array.isArray(steps) ? (steps as Row[]) : []).map(mapStep),
    jobId: str(row, 'jobId', 'JobId'),
  };
};

const mapJob = (row: Row): AnalysisStepJob | null => {
  const id = str(row, 'id', 'Id');
  const requestedAt = str(row, 'requestedAt', 'RequestedAt');
  if (!id || !requestedAt) return null;
  return {
    id,
    kind: str(row, 'kind', 'Kind') ?? 'detection',
    provider: str(row, 'provider', 'Provider'),
    detector: str(row, 'detector', 'Detector'),
    modelVersion: str(row, 'modelVersion', 'ModelVersion'),
    status: str(row, 'status', 'Status') ?? 'planned',
    waitingOn: str(row, 'waitingOn', 'WaitingOn'),
    requestedAt,
    dispatchedAt: str(row, 'dispatchedAt', 'DispatchedAt'),
    finishedAt: str(row, 'finishedAt', 'FinishedAt'),
    workerSeconds: num(row, 'workerSeconds', 'WorkerSeconds'),
    workerId: str(row, 'workerId', 'WorkerId'),
    error: str(row, 'error', 'Error'),
    decided: (row.decided ?? row.Decided) === true,
    attempt: num(row, 'attempt', 'Attempt') ?? 1,
  };
};

const mapOsmFeatures = (value: unknown): AnalysisOsmFeatures | null => {
  if (!value || typeof value !== 'object') return null;
  const row = value as Row;
  return {
    greens: num(row, 'greens', 'Greens') ?? 0,
    holes: num(row, 'holes', 'Holes') ?? 0,
    bunkers: num(row, 'bunkers', 'Bunkers') ?? 0,
    fairways: num(row, 'fairways', 'Fairways') ?? 0,
  };
};

const mapScorecardImport = (value: unknown): AnalysisScorecardImport | null => {
  if (!value || typeof value !== 'object') return null;
  const row = value as Row;
  const importedAtUtc = str(row, 'importedAtUtc', 'ImportedAtUtc');
  if (!importedAtUtc) return null;
  return {
    importedAtUtc,
    holes: num(row, 'holes', 'Holes') ?? 0,
    tees: num(row, 'tees', 'Tees') ?? 0,
  };
};

export const mapAnalysisStepsResponse = (body: unknown): AnalysisRunSteps | null => {
  if (!body || typeof body !== 'object') return null;
  const root = body as Row;
  const runId = str(root, 'runId', 'RunId');
  if (!runId) return null;

  const providers = root.providers ?? root.Providers;
  const jobs = root.jobs ?? root.Jobs;
  return {
    runId,
    courseId: str(root, 'courseId', 'CourseId') ?? '',
    courseName: str(root, 'courseName', 'CourseName'),
    status: str(root, 'status', 'Status') ?? 'unknown',
    detector: str(root, 'detector', 'Detector') ?? 'classical',
    modelVersion: str(root, 'modelVersion', 'ModelVersion'),
    startedAt: str(root, 'startedAt', 'StartedAt') ?? '',
    finishedAt: str(root, 'finishedAt', 'FinishedAt'),
    durationSeconds: num(root, 'durationSeconds', 'DurationSeconds'),
    error: str(root, 'error', 'Error'),
    providers: (Array.isArray(providers) ? (providers as Row[]) : []).map(mapProvider),
    jobs: (Array.isArray(jobs) ? (jobs as Row[]) : [])
      .map(mapJob)
      .filter((job): job is AnalysisStepJob => job !== null),
    requestedAt: str(root, 'requestedAt', 'RequestedAt'),
    correlationId: str(root, 'correlationId', 'CorrelationId'),
    osmFeatures: mapOsmFeatures(root.osmFeatures ?? root.OsmFeatures),
    scorecardImport: mapScorecardImport(root.scorecardImport ?? root.ScorecardImport),
  };
};

export function useAnalysisSteps(
  apiBaseUrl: string,
  token: string | null,
  runId: string | null
): AnalysisStepsState {
  const [state, setState] = useState<AnalysisStepsState>({ status: 'loading' });

  useEffect(() => {
    if (!runId || !token) {
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/analysis-runs/${runId}/steps`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        if (response.status === 401 || response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        // A run from before this was recorded is not a failure: it simply has no stages.
        if (response.status === 404) {
          setState({ status: 'empty' });
          return;
        }
        if (!response.ok) {
          setState({ status: 'failed' });
          return;
        }

        const run = mapAnalysisStepsResponse(await response.json());
        if (cancelled) return;
        // A run with jobs but no stages yet is in flight, not empty: its lanes are the
        // thing to show while the first detection is still out.
        if (!run || (run.jobs.length === 0 && run.providers.every((provider) => provider.steps.length === 0))) {
          setState({ status: 'empty' });
          return;
        }

        setState({ status: 'ready', run });
      } catch {
        if (!cancelled) setState({ status: 'failed' });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, token, runId]);

  return state;
}
